import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons'; 
import { resetPassword } from '../services/AuthService';
import { validatePassword, validatePasswordMatch } from '../services/Validation'; 
import { useAuth } from "../services/AuthContext";
import { useTranslation } from 'react-i18next';
import '../css/forms.css';

const ResetPasswordModal = ({ show, handleClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const { resetToken } = useAuth();

  const navigate = useNavigate();
   const newPasswordRef = useRef(null);

  const handleResetPassword = async () => {

    // Valider le nouveau mot de passe
    const passwordValidation = validatePassword(newPassword);
    const passwordMatchValidation = validatePasswordMatch(newPassword, confirmPassword);

    // Si l'une des validations échoue, afficher l'erreur et arrêter
    if (!passwordValidation.valid || !passwordMatchValidation.valid) {
      setErrors({
        newPassword: passwordValidation.error,
        confirmPassword: passwordMatchValidation.error
      });
      return;
    }

    // Réinitialiser les erreurs si tout est valide
    setErrors({});

    try {
      await resetPassword(resetToken, newPassword);
      setMessage('Mot de passe réinitialisé avec succès.');
      setTimeout(() => {
        handleClose();
        navigate('/');
      }, 2000); // Fermer la modale après 2 secondes
    } catch (error) {
      setMessage(error.message || 'Erreur lors de la réinitialisation du mot de passe.');
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      restoreFocus={false}
      onEntered={() => {
        newPasswordRef.current?.focus();
      }}
    >
      <Modal.Header>
        <Modal.Title>{t('modal.resetpassword.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="newPassword">
            <Form.Label>{t('modal.resetpassword.newpassword.value')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('modal.resetpassword.newpassword.default')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  isInvalid={!!errors.newPassword}
                  ref={newPasswordRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      document.getElementById('confirmPassword')?.focus();
                    }
                  }}
                />
                <Button
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>     
                <Form.Control.Feedback type="invalid">
                  {errors.newPassword}
                </Form.Control.Feedback>         
              </InputGroup>
          </Form.Group>
          <Form.Group controlId="confirmPassword">
            <Form.Label>{t('modal.resetpassword.confirmpassword.value')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('modal.resetpassword.confirmpassword.default')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  isInvalid={!!errors.confirmPassword}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleResetPassword();
                    }
                  }}
                />
                <Button
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>              
                <Form.Control.Feedback type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              </InputGroup>
          </Form.Group>
          <Button variant="primary" onClick={handleResetPassword}>
            {t('modal.resetpassword.reset')}
          </Button>
        </Form>
        {message && (
              <p
                style={{
                  marginTop: '10px',
                  color: '#ff4d4f',        // rouge vif pour l’erreur
                  fontWeight: 'bold',      // un peu plus visible
                  padding: '8px 12px',
                  borderRadius: '5px',
                }}
              >
                {message}
              </p>
            )}
      </Modal.Body>
    </Modal>
  );
};

export default ResetPasswordModal;
